import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchAirtimeCategories,
  deleteAirtimeCategory,
} from "../../../../features/auth/store/airtimeCategorySlice";
import AirtimeCategoriesTable from "./AirtimeCategoriesTable";
import { CreateAirtimeCategoryModal } from "../../Modal/Airtime/CreateAirtimeCategoryModal";
import {
  ExternalContainer,
  InternalContainer,
} from "../../../../components/Reusable/Layout";
import { Button } from "../../../../components/Reusable/Button";
import { EditAirtimeCategoryModal } from "../../Modal/Airtime/EditAirtimeCategoryModal";
import { DeleteAirtimeCategoryModal } from "../../Modal/Airtime/DeleteAirtimeCategoryModal";

const AirtimeCategories: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);

  const { airtimeCategories, loading, error } = useSelector(
    (state: RootState) => state.airtimeCategories
  );

  const openDeleteModal = async (id: number) => {
    setSelectedCategory(id);
    setIsDeleteModalOpen(true);
  };

  const openEditModal = async (id: number) => {
    setSelectedCategory(id);
    setIsEditModalOpen(true);
  };

  useEffect(() => {
    dispatch(fetchAirtimeCategories({}));
  }, [dispatch]);

  const handleDelete = async (id: number) => {
    try {
      await dispatch(deleteAirtimeCategory(id)).unwrap();
      toast.success("Category deleted!");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete category");
    }
  };

  // Filter and search logic
  const filteredCategories = airtimeCategories.filter((category) => {
    const matchesStatus = !statusFilter || category.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      category.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  return (
    <ExternalContainer>
      <div className="h-screen dark:bg-[#FFFFFF1A] w-[1030px]">
        <InternalContainer headerText="Airtime Categories">
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-4 mb-4 w-full">
              <input
                type="text"
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border p-2 rounded"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border p-2 rounded"
              >
                <option value="">All Status</option>
                <option value="on">Active</option>
                <option value="off">Inactive</option>
              </select>
            </div>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              bgColor="bg-gradient-to-r from-[#027EC2] to-[#039EF2]"
              textColor="text-white"
              borderRadius="rounded-lg"
              className="w-[200px] h-10 ml-6"
            >
              New Category
            </Button>
          </div>

          <AirtimeCategoriesTable
            categories={filteredCategories} // Use filtered categories here
            loading={loading}
            onDelete={openDeleteModal}
            onEdit={openEditModal}
          />

          <CreateAirtimeCategoryModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
          />
        </InternalContainer>

        {/* Fixed modal rendering */}
        <EditAirtimeCategoryModal
          isOpen={isEditModalOpen}
          id={selectedCategory}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedCategory(null);
          }}
        />

        <DeleteAirtimeCategoryModal
          id={selectedCategory}
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedCategory(null);
          }}
        />
      </div>
    </ExternalContainer>
  );
};

export default AirtimeCategories;
